import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Search,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  Check,
  Users,
  Star,
  Calendar,
  ArrowUpDown,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  FileSpreadsheet,
  Upload,
  Music,

} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { ROSTER_API, ROUTINES_API, fetchWithAuth, api } from "../../utils/api";
import FormModal from "../../components/roster_user/FormModal";
import BulkImportModal from "../../components/roster_user/BulkImportModal";
import { SkeletonStudioPageContent } from '../../components/ui/SkeletonLoader';

// 🚀 ATOMIC DEDUPLICATION: Promise-based approach that's truly atomic
let dataFetchPromise = null;
let cachedData = null;

// Role color mapping
const ROLE_COLORS = {
  Teacher: "bg-emerald-100 text-emerald-800",
  Director: "bg-purple-100 text-purple-800",
  Dancer: "bg-blue-100 text-blue-800",
  default: "bg-gray-100 text-gray-800",
};

const StudioPage = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [roster, setRoster] = useState([]);
  // NEW: also store routines
  const [routines, setRoutines] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, message: '' });

  // For routine details modal
  const [isRoutineModalOpen, setIsRoutineModalOpen] = useState(false);
  const [selectedMemberRoutines, setSelectedMemberRoutines] = useState([]);

  // For hover preview
  const [hoveredMember, setHoveredMember] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  // Simplified form state for adding new members
  const [newMemberForm, setNewMemberForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    role: [], // Multiple roles supported
    dateOfBirth: "",
    profilePicture: "",
  });

  // Form state for editing existing member (kept separate)
  const [editMemberForm, setEditMemberForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: [],
    dateOfBirth: "",
    profilePicture: "",
  });

  // For filtering/sorting
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);

  // For pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  // -------------------------
  // 🚀 ATOMIC DEDUPLICATION: Promise-based solution that's truly race-condition proof
  // -------------------------
  useEffect(() => {
    let isCancelled = false;
    
    const fetchData = async () => {
      try {
        // 🚀 ATOMIC SOLUTION: If there's already a promise, wait for it
        if (dataFetchPromise) {
          console.log("📊 Fetch already in progress, waiting for existing promise...");
          const data = await dataFetchPromise;
          if (!isCancelled && data) {
            console.log("📊 Using data from existing fetch");
            setRoster(data.roster || []);
            setRoutines(data.routines || []);
            setLoading(false);
          }
          return;
        }
        
        // If we have cached data, use it
        if (cachedData) {
          console.log("📊 Using cached data, skipping API calls");
          setRoster(cachedData.roster || []);
          setRoutines(cachedData.routines || []);
          setLoading(false);
          return;
        }
        
        console.log("📡 Fetching roster and routines data...");
        setLoading(true);
        setError(null);
        
        // Create the fetch promise
        dataFetchPromise = (async () => {
          const controller = new AbortController();
          const [rosterRes, routinesRes] = await Promise.all([
            fetchWithAuth(ROSTER_API, { signal: controller.signal }),
            fetchWithAuth(ROUTINES_API, { signal: controller.signal }),
          ]);
          
          const data = {
            roster: rosterRes.roster || [],
            routines: routinesRes.routines || []
          };
          
          // Cache the data
          cachedData = data;
          
          console.log("✅ API Response: Roster count:", data.roster.length);
          console.log("✅ API Response: Routines count:", data.routines.length);
          
          return data;
        })();
        
        // Wait for the promise and update state
        const data = await dataFetchPromise;
        
        if (!isCancelled) {
          setRoster(data.roster);
          setRoutines(data.routines);
        }
        
      } catch (err) {
        if (!isCancelled && err.name !== 'AbortError') {
          console.error("❌ Error fetching data:", err);
          setError(err.message);
        }
        // Clear the promise on error so it can be retried
        dataFetchPromise = null;
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
        // Clear the promise when done (successful or failed)
        dataFetchPromise = null;
      }
    };
    
    fetchData();
    
    // Cleanup function to prevent memory leaks
    return () => {
      isCancelled = true;
    };
  }, []); // Empty dependency array ensures this only runs once

  // 🚀 OPTIMIZED: Helper to parse dancer roles with memoization
  const normalizeRoles = useCallback((roleValue) => {
    if (!roleValue) return [];
    
    // Handle string arrays (normal case)
    if (Array.isArray(roleValue) && roleValue.length > 0 && typeof roleValue[0] === "string") {
      return roleValue;
    }
    
    // Handle DynamoDB format: [{S: "Dancer"}]
    if (Array.isArray(roleValue) && roleValue.length > 0 && typeof roleValue[0] === "object" && roleValue[0]?.S) {
      return roleValue.map((r) => r.S);
    }
    
    // Handle single string
    if (typeof roleValue === "string") {
      return [roleValue];
    }
    
    // Handle single object case
    if (typeof roleValue === 'object' && roleValue?.S) {
      return [roleValue.S];
    }
    
    // Handle malformed objects - extract any S values
    if (Array.isArray(roleValue)) {
      const processed = roleValue.map(item => {
        if (typeof item === 'string') return item;
        if (typeof item === 'object' && item?.S) return item.S;
        console.warn('🚨 Unknown role item format:', item);
        return 'Unknown'; // Fallback for malformed data
      });
      return processed.filter(role => role !== 'Unknown'); // Remove unknown entries
    }
    
    // Handle the case where role is a plain object without S property (the [object Object] issue)
    if (typeof roleValue === 'object' && !roleValue?.S && !Array.isArray(roleValue)) {
      console.error('🚨 FOUND THE CULPRIT - Plain object role without S property:', roleValue);
      // If it's a plain object, try to extract meaningful values
      if (roleValue.toString() === '[object Object]') {
        console.error('🚨 This is the [object Object] issue!');
        // Try to find any string properties
        const values = Object.values(roleValue).filter(v => typeof v === 'string');
        if (values.length > 0) {
          return values;
        }
      }
      return ['Unknown'];
    }
    
    // Fallback for any other case
    console.warn('🚨 Unexpected role format:', roleValue, 'type:', typeof roleValue);
    return [];
  }, []);

  // Helper to parse routine's dancer list (similar logic)
  const normalizeDancerList = (dancers) => {
    if (!dancers) return [];
    
    // Handle string arrays (normal case)
    if (Array.isArray(dancers) && dancers.length > 0 && typeof dancers[0] === "string") {
      return dancers;
    }
    
    // Handle DynamoDB format: [{S: "dancerId"}]
    if (Array.isArray(dancers) && dancers.length > 0 && typeof dancers[0] === "object" && dancers[0]?.S) {
      return dancers.map((d) => d.S);
    }
    
    // Handle single string
    if (typeof dancers === "string") {
      return [dancers];
    }
    
    // Handle single object case
    if (typeof dancers === 'object' && dancers?.S) {
      return [dancers.S];
    }
    
    return Array.isArray(dancers) ? dancers : [];
  };

  // 🚀 OPTIMIZED: Pre-compute routine counts to avoid repeated calculations
  const routineCounts = useMemo(() => {
    const counts = {};
    
    routines.forEach(routine => {
      const routineDancers = normalizeDancerList(routine.dancers);
      routineDancers.forEach(dancerId => {
        counts[dancerId] = (counts[dancerId] || 0) + 1;
      });
    });
    
    return counts;
  }, [routines]);

  // For counting how many routines a given dancer is in
  const countRoutines = useCallback((dancerId) => {
    return routineCounts[dancerId] || 0;
  }, [routineCounts]);

  // 🚀 NEW: Get specific routines for a dancer
  const getDancerRoutines = useCallback((dancerId) => {
    return routines.filter(routine => {
      const routineDancers = normalizeDancerList(routine.dancers);
      return routineDancers.includes(dancerId);
    });
  }, [routines]);

  // Handle clicking on routine count to show details
  const handleRoutineCountClick = useCallback((member) => {
    const memberRoutines = getDancerRoutines(member.dancerId);
    console.log('🎭 Selected member routines:', memberRoutines);
    console.log('🎭 First routine structure:', memberRoutines[0]);
    setSelectedMember(member);
    setSelectedMemberRoutines(memberRoutines);
    setIsRoutineModalOpen(true);
  }, [getDancerRoutines]);

  // Check if device supports hover (desktop)
  const supportsHover = useMemo(() => {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }, []);

  // Handle hover preview (only on desktop)
  const handleRoutineHover = useCallback((member) => {
    if (!supportsHover) return; // Skip on mobile/tablet
    
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    // Set a small delay before showing preview
    const timeout = setTimeout(() => {
      setHoveredMember(member);
    }, 300); // 300ms delay
    
    setHoverTimeout(timeout);
  }, [hoverTimeout, supportsHover]);

  const handleRoutineHoverLeave = useCallback(() => {
    if (!supportsHover) return; // Skip on mobile/tablet
    
    // Add a delay before hiding to allow mouse movement to tooltip
    const timeout = setTimeout(() => {
      setHoveredMember(null);
    }, 100); // Short delay
    
    setHoverTimeout(timeout);
  }, [supportsHover]);

  const handleTooltipHover = useCallback(() => {
    if (!supportsHover) return;
    
    // Cancel any pending hide timeout when entering tooltip
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout, supportsHover]);

  const handleTooltipLeave = useCallback(() => {
    if (!supportsHover) return;
    
    // Hide immediately when leaving tooltip
    setHoveredMember(null);
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout, supportsHover]);

  // Handle profile picture upload with compression
  const handleProfilePictureUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic validation
    if (file.size > 10 * 1024 * 1024) { // 10MB limit for initial file
      setError("Profile picture must be less than 10MB");
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError("Please select a valid image file");
      return;
    }

    setIsProcessingImage(true);
    setError(null);

    // Compress and resize the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions (max 400x400 for profile photos)
      const maxSize = 400;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to blob with compression (0.8 quality for good balance)
      canvas.toBlob((blob) => {
        if (blob) {
          // Check compressed size
          if (blob.size > 500 * 1024) { // 500KB limit
            setIsProcessingImage(false);
            setError("Compressed image is still too large. Please use a smaller image.");
            return;
          }

          // Convert to base64
          const reader = new FileReader();
          reader.onload = (event) => {
            setNewMemberForm(prev => ({
              ...prev,
              profilePicture: event.target.result
            }));
            setIsProcessingImage(false);
            setError(null); // Clear any previous errors
          };
          reader.readAsDataURL(blob);
        }
      }, 'image/jpeg', 0.8); // Convert to JPEG with 80% quality
    };

    img.onerror = () => {
      setIsProcessingImage(false);
      setError("Failed to process image. Please try a different image.");
    };

    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, []);

  // Remove profile picture
  const removeProfilePicture = useCallback(() => {
    setNewMemberForm(prev => ({ ...prev, profilePicture: "" }));
  }, []);

  // Toggle role for new member form
  const toggleNewMemberRole = useCallback((roleValue) => {
    setNewMemberForm(prev => {
      const currentRoles = prev.role || [];
      const newRoles = currentRoles.includes(roleValue)
        ? currentRoles.filter(r => r !== roleValue)
        : [...currentRoles, roleValue];
      return { ...prev, role: newRoles };
    });
  }, []);

  // Toggle role for edit member form
  const toggleEditMemberRole = useCallback((roleValue) => {
    setEditMemberForm(prev => {
      const currentRoles = prev.role || [];
      const newRoles = currentRoles.includes(roleValue)
        ? currentRoles.filter(r => r !== roleValue)
        : [...currentRoles, roleValue];
      return { ...prev, role: newRoles };
    });
  }, []);

  // Handle profile photo upload for edit form
  const handleEditPhotoUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      // Basic validation
      if (file.size > 15 * 1024 * 1024) {
        setError('Image is too large. Please select an image under 15MB.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file.');
        return;
      }

      // Convert to base64 for upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditMemberForm(prev => ({ 
          ...prev, 
          profilePicture: e.target.result 
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // 2) Outside click to close filter
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isFilterOpen && !event.target.closest('.filter-container')) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterOpen]);

  // 3) Add member (POST) - simplified for inline form
  const handleAddMember = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!newMemberForm.firstName.trim() || !newMemberForm.lastName.trim()) {
      setError("First name and last name are required");
      return;
    }

    if (!newMemberForm.role || newMemberForm.role.length === 0) {
      setError("Please select at least one role");
      return;
    }

    if (newMemberForm.role.includes("Dancer") && !newMemberForm.dateOfBirth) {
      setError("Date of birth is required for dancers");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      const formattedMember = {
        firstName: newMemberForm.firstName.trim(),
        lastName: newMemberForm.lastName.trim(),
        email: newMemberForm.email.trim() || "", // Ensure empty string if no email
        gender: newMemberForm.gender,
        role: newMemberForm.role, // Send as regular array
        dateOfBirth: newMemberForm.dateOfBirth,
        profilePhoto: newMemberForm.profilePicture || null, // Lambda expects 'profilePhoto'
      };

      console.log("Sending member data to API:", formattedMember);

      const newMember = await fetchWithAuth(ROSTER_API, {
        method: "POST",
        body: JSON.stringify(formattedMember),
      });
      
      // 🚀 OPTIMIZATION: Add to existing roster instead of full re-fetch
      setRoster(prevRoster => [...prevRoster, newMember]);
      
      // Reset form
      resetNewMemberForm();
    } catch (err) {
      console.error("Error adding member:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 4) Update member (PUT)
  const handleUpdateMember = async (e) => {
    e?.preventDefault();
    try {
      setIsSubmitting(true);
      const memberId = selectedMember.dancerId;
      const formattedMember = {
        ...editMemberForm,
        role: editMemberForm.role, // Send as regular array  
        profilePhoto: editMemberForm.profilePicture === "" ? "REMOVE_PHOTO" : editMemberForm.profilePicture || null, // Lambda expects 'profilePhoto'
      };

      console.log("Updating member data:", formattedMember);

      const updatedMember = await fetchWithAuth(`${ROSTER_API}/${memberId}`, {
        method: "PUT",
        body: JSON.stringify(formattedMember),
      });
      
      // 🚀 OPTIMIZATION: Update specific member instead of full re-fetch
      setRoster(prevRoster => 
        prevRoster.map(member => 
          member.dancerId === memberId ? updatedMember : member
        )
      );
      setEditingMemberId(null);
      setSelectedMember(null);
      resetEditForm();
    } catch (err) {
      console.error("Error updating member:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get routines for deletion confirmation
  const getMemberRoutinesForDeletion = useCallback((member) => {
    const memberRoutines = getDancerRoutines(member.dancerId);
    return {
      memberRoutines,
      totalRoutines: memberRoutines.length,
      hasRoutines: memberRoutines.length > 0
    };
  }, [getDancerRoutines]);

  // 5) Delete member (DELETE) - Enhanced with protection
  const handleDeleteMember = async () => {
    try {
      const memberId = selectedMember.dancerId;
      await fetchWithAuth(`${ROSTER_API}/${memberId}`, {
        method: "DELETE"
      });
      
      // 🚀 OPTIMIZATION: Remove from existing roster instead of full re-fetch
      setRoster(prevRoster => 
        prevRoster.filter(member => member.dancerId !== memberId)
      );
      
      // 🔄 REFRESH: Refetch routines to get updated data from backend after cleanup
      try {
        const routinesData = await fetchWithAuth(ROUTINES_API);
        setRoutines(routinesData.routines || []);
      } catch (routineError) {
        console.warn('Failed to refresh routines after dancer deletion:', routineError);
      }
      
      setIsDeleteModalOpen(false);
      setSelectedMember(null);
    } catch (err) {
      console.error("Error deleting member:", err);
      setError(err.message);
    }
  };

  // 6) Bulk import members - Optimized approach
  const handleBulkImport = async (members) => {
    try {
      setIsSubmitting(true);
      setImportProgress({ current: 0, total: members.length, message: 'Preparing import...' });
      
      // Format all members for bulk import
      const formattedMembers = members.map(member => ({
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email || '',
        dateOfBirth: member.dateOfBirth || '',
        gender: member.gender || '',
        role: Array.isArray(member.role) ? member.role : [member.role],
        profilePhoto: null // No photos in bulk import for now
      }));

      try {
        // Try bulk import first (most efficient)
        setImportProgress({ current: 0, total: members.length, message: 'Importing all dancers...' });
        
        const bulkResult = await api.bulkCreateRosterMembers(formattedMembers);
        
        setImportProgress({ current: members.length, total: members.length, message: 'Import complete!' });
        
        // 🚀 OPTIMIZATION: For bulk import, we'll still need to refresh since we can't predict the exact return format
        // But we'll make it conditional on success
        if (bulkResult && bulkResult.successCount > 0) {
          const data = await fetchWithAuth(ROSTER_API);
          setRoster(data.roster || []);
        }
        
        console.log(`✅ Bulk import successful: ${members.length} dancers imported`);
        
      } catch (bulkError) {
        console.warn('⚠️ Bulk import failed, falling back to individual requests:', bulkError.message);
        
        // Fallback to individual requests if bulk endpoint doesn't exist yet
        const importResults = [];
        const errors = [];
        
        for (let i = 0; i < formattedMembers.length; i++) {
          const member = formattedMembers[i];
          const originalMember = members[i];
          
          setImportProgress({ 
            current: i + 1, 
            total: members.length, 
            message: `Importing ${originalMember.firstName} ${originalMember.lastName}...` 
          });
          
          try {
            const newMember = await fetchWithAuth(ROSTER_API, {
              method: "POST",
              body: JSON.stringify(member),
            });
            
            importResults.push(newMember);
          } catch (memberError) {
            errors.push({
              member: `${originalMember.firstName} ${originalMember.lastName}`,
              error: memberError.message
            });
          }
        }

        // 🚀 OPTIMIZATION: For fallback individual imports, still refresh but only if we had successes
        if (importResults.length > 0) {
          const data = await fetchWithAuth(ROSTER_API);
          setRoster(data.roster || []);
        }
        
        // Show success/error feedback for fallback method
        if (errors.length > 0) {
          setError(`Imported ${importResults.length} members successfully. ${errors.length} failed: ${errors.map(e => e.member).join(', ')}`);
        }
        
        console.log(`⚠️ Fallback import completed: ${importResults.length} successful, ${errors.length} failed`);
      }
      
      setIsBulkImportModalOpen(false);
      
    } catch (err) {
      console.error("Error during bulk import:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
      setImportProgress({ current: 0, total: 0, message: '' });
    }
  };

  // 6) Reset forms
  const resetNewMemberForm = useCallback(() => {
    setNewMemberForm({
      firstName: "",
      lastName: "",
      email: "",
      gender: "",
      role: [],
      dateOfBirth: "",
      profilePicture: "",
    });
  }, []);

  const resetEditForm = useCallback(() => {
    setEditMemberForm({
      firstName: "",
      lastName: "",
      email: "",
      role: [],
      dateOfBirth: "",
      profilePicture: "",
    });
  }, []);

  // 🚀 OPTIMIZED: Pre-normalize roles and cache member data
  const normalizedRoster = useMemo(() => {
    return roster.map(member => ({
      ...member,
      normalizedRoles: normalizeRoles(member.role),
      searchableText: `${member.firstName} ${member.lastName} ${member.email || ''}`.toLowerCase()
    }));
  }, [roster, normalizeRoles]);

  // Filter logic - now uses pre-normalized data
  const filteredRoster = useMemo(() => {
    if (!searchQuery) return normalizedRoster;
    
    const searchString = searchQuery.toLowerCase();
    return normalizedRoster.filter((member) => {
      return (
        member.searchableText.includes(searchString) ||
        member.normalizedRoles.some((role) => role.toLowerCase().includes(searchString))
      );
    });
  }, [normalizedRoster, searchQuery]);

  // Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // role-based filter
  const toggleRole = useCallback((role) => {
    setSelectedRoles(prev => 
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  }, []);

  // final sorted + filtered data
  const getSortedAndFilteredRoster = useMemo(() => {
    let filteredData = [...filteredRoster];

    // role-based filter - now uses pre-normalized roles
    if (selectedRoles.length > 0) {
      filteredData = filteredData.filter(member => 
        member.normalizedRoles.some(role => 
          selectedRoles.includes(role)
        )
      );
    }

    // sorting
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        if (sortConfig.key === 'name') {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return sortConfig.direction === 'asc' 
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        }
        if (sortConfig.key === 'email') {
          return sortConfig.direction === 'asc'
            ? a.email.localeCompare(b.email)
            : b.email.localeCompare(a.email);
        }
        if (sortConfig.key === 'role') {
          const rolesA = a.normalizedRoles.join(', ');
          const rolesB = b.normalizedRoles.join(', ');
          return sortConfig.direction === 'asc'
            ? rolesA.localeCompare(rolesB)
            : rolesB.localeCompare(rolesA);
        }
        return 0;
      });
    }

    return filteredData;
  }, [filteredRoster, selectedRoles, sortConfig]);

  // Pagination calculations
  const totalItems = getSortedAndFilteredRoster.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = getSortedAndFilteredRoster.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedRoles, sortConfig]);

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // editing - now uses pre-normalized roles
  const handleEditClick = useCallback((member) => {
    setSelectedMember(member);
    setEditMemberForm({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      role: member.normalizedRoles || normalizeRoles(member.role),
      dateOfBirth: member.dateOfBirth,
      profilePicture: member.profilePicture,
      gender: member.gender,
    });
    setEditingMemberId(member.dancerId);
  }, [normalizeRoles]);

  const getRoleColor = useCallback((role) => {
    return ROLE_COLORS[role] || ROLE_COLORS.default;
  }, []);

  // Calculate age
  const calculateAge = useCallback((birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  }, []);



  // handle close modals
  const handleCancelEdit = useCallback(() => {
    setEditingMemberId(null);
    setSelectedMember(null);
    resetEditForm();
  }, [resetEditForm]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-10 md:pt-0">
          <SkeletonStudioPageContent />
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-10 md:pt-0">
          <div className={`p-4 rounded-lg ${
            isDarkMode 
              ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
              : 'alert alert-error shadow-lg'
          }`}>
            <div className="flex items-center">
              <AlertCircle className={`h-6 w-6 ${isDarkMode ? 'text-red-400' : ''}`} />
              <span>{error}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RENDER
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col min-h-[calc(100vh-2rem)] space-y-4 pt-10 md:pt-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
        <div>
          <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>Studio Roster</h1>
          <p className={`text-sm md:text-base ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
            Manage your dance studio members and staff
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setIsBulkImportModalOpen(true)}
            className="btn btn-outline btn-primary"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Bulk Import Dancers
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {/* Total Members */}
        <div
                  className={`rounded-xl shadow ${
          isDarkMode
            ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
            : "card bg-base-100"
        }`}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div
                className={`p-2 rounded-lg bg-primary/10`}
              >
                <Users
                  className={`h-6 w-6 text-primary`}
                />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt
                    className={`text-sm font-medium truncate ${
                      isDarkMode ? "text-gray-400" : "text-base-content/70"
                    }`}
                  >
                    Total Members
                  </dt>
                  <dd
                    className={`text-lg font-medium ${
                      isDarkMode ? "text-white" : "text-base-content"
                    }`}
                  >
                    {roster.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* # of Dancers */}
        <div
                  className={`rounded-xl shadow ${
          isDarkMode
            ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
            : "card bg-base-100"
        }`}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div
                className={`p-2 rounded-lg ${
                  isDarkMode ? "bg-violet-500/10" : "bg-info/10"
                }`}
              >
                <Star
                  className={`h-6 w-6 ${
                    isDarkMode ? "text-violet-400" : "text-info"
                  }`}
                />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt
                    className={`text-sm font-medium truncate ${
                      isDarkMode ? "text-gray-400" : "text-base-content/70"
                    }`}
                  >
                    Dancers
                  </dt>
                  <dd
                    className={`text-lg font-medium ${
                      isDarkMode ? "text-white" : "text-base-content"
                    }`}
                  >
                    {normalizedRoster.filter((member) =>
                      member.normalizedRoles.includes("Dancer")
                    ).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* # Teachers & Directors */}
        <div
                  className={`rounded-xl shadow ${
          isDarkMode
            ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
            : "card bg-base-100"
        }`}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div
                className={`p-2 rounded-lg ${
                  isDarkMode ? "bg-green-500/10" : "bg-success/10"
                }`}
              >
                <Calendar
                  className={`h-6 w-6 ${
                    isDarkMode ? "text-green-400" : "text-success"
                  }`}
                />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt
                    className={`text-sm font-medium truncate ${
                      isDarkMode ? "text-gray-400" : "text-base-content/70"
                    }`}
                  >
                    Teachers & Directors
                  </dt>
                  <dd
                    className={`text-lg font-medium ${
                      isDarkMode ? "text-white" : "text-base-content"
                    }`}
                  >
                    {
                      normalizedRoster.filter((member) =>
                        member.normalizedRoles.some((role) =>
                          ["Teacher", "Director"].includes(role)
                        )
                      ).length
                    }
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Member Section */}
      <div
        className={`rounded-xl shadow p-4 ${
          isDarkMode
            ? "bg-card-gradient backdrop-blur-sm border border-brand-border-logo shadow-lg"
            : "card bg-base-100"
        }`}
      >
        <div className="mb-3">
          <div className="flex items-center gap-3">
            <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-base-content"}`}>
              Add New Member
            </h2>
            <span className={`text-sm ${isDarkMode ? "text-red-400" : "text-error"}`}>
              * Required fields
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-base-content/70"}`}>
            Add dancers, teachers, and staff to your studio roster. These members will be available for future event registrations.
          </p>
        </div>

        <form onSubmit={handleAddMember} className="space-y-3">
          <div className="grid gap-2 items-end" style={{gridTemplateColumns: "auto 1fr 1fr 1fr 1fr 1fr 1fr auto"}}>
            {/* Profile Photo Circle */}
            <div className="form-control w-fit">
              <label className="label">
                <span className={`label-text ${isDarkMode ? "text-gray-300" : ""}`}>
                  Photo
                </span>
              </label>
              <div className="flex">
                {isProcessingImage ? (
                  <div className={`w-12 h-12 rounded-full border-2 border-dashed flex items-center justify-center ${
                    isDarkMode 
                      ? "border-primary bg-[#1a1f2d]" 
                      : "border-primary bg-base-100"
                  }`}>
                    <div className="loading loading-spinner loading-sm text-primary"></div>
                  </div>
                ) : newMemberForm.profilePicture ? (
                  <div className="relative">
                    <img
                      src={newMemberForm.profilePicture}
                      alt="Profile"
                      className="w-12 h-12 rounded-full object-cover border-2 border-primary"
                    />
                    <button
                      type="button"
                      onClick={removeProfilePicture}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white rounded-full flex items-center justify-center text-xs hover:bg-error/80 transition-colors"
                      title="Remove photo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className={`w-12 h-12 rounded-full border-2 border-dashed cursor-pointer flex items-center justify-center transition-all duration-200 hover:scale-105 ${
                    isDarkMode 
                      ? "border-gray-600 hover:border-primary bg-[#1a1f2d] hover:bg-primary/10" 
                      : "border-gray-300 hover:border-primary bg-base-100 hover:bg-primary/5"
                  }`}>
                    <Plus className={`w-5 h-5 ${isDarkMode ? "text-gray-500 hover:text-primary" : "text-gray-400 hover:text-primary"}`} />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureUpload}
                      className="hidden"
                      disabled={isProcessingImage}
                    />
                  </label>
                )}
              </div>
            </div>

            {/* First Name */}
            <div className="form-control">
              <label className="label">
                <span className={`label-text ${isDarkMode ? "text-gray-300" : ""}`}>
                  First Name <span className="text-red-500">*</span>
                </span>
              </label>
              <input
                type="text"
                placeholder="First Name"
                value={newMemberForm.firstName}
                onChange={(e) => setNewMemberForm(prev => ({ ...prev, firstName: e.target.value }))}
                className={`input input-bordered w-full ${
                  isDarkMode 
                    ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content placeholder-gray-500" 
                    : ""
                }`}
                required
              />
            </div>

            {/* Last Name */}
            <div className="form-control">
              <label className="label">
                <span className={`label-text ${isDarkMode ? "text-gray-300" : ""}`}>
                  Last Name <span className="text-red-500">*</span>
                </span>
              </label>
              <input
                type="text"
                placeholder="Last Name"
                value={newMemberForm.lastName}
                onChange={(e) => setNewMemberForm(prev => ({ ...prev, lastName: e.target.value }))}
                className={`input input-bordered w-full ${
                  isDarkMode 
                    ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content placeholder-gray-500" 
                    : ""
                }`}
                required
              />
            </div>

            {/* Gender */}
            <div className="form-control">
              <label className="label">
                <span className={`label-text ${isDarkMode ? "text-gray-300" : ""}`}>
                  Gender <span className="text-red-500">*</span>
                </span>
              </label>
              <div className="relative">
                <select
                  value={newMemberForm.gender}
                  onChange={(e) => setNewMemberForm(prev => ({ ...prev, gender: e.target.value }))}
                  className={`select select-bordered w-full ${
                    isDarkMode 
                      ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content" 
                      : ""
                  }`}
                  required
                >
                  <option value="">Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Non-binary">Non-binary</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
                <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-base-content/50"}`} />
              </div>
            </div>

            {/* Roles Multi-Select */}
            <div className="form-control">
              <label className="label">
                <span className={`label-text ${isDarkMode ? "text-gray-300" : ""}`}>
                  Roles <span className="text-red-500">*</span>
                </span>
              </label>
              <div className="dropdown dropdown-bottom">
                <label tabIndex={0} className={`btn btn-outline w-full justify-between ${
                  isDarkMode ? "border-white/10 hover:bg-white/5" : ""
                }`}>
                  <span className="truncate">
                    {newMemberForm.role.length === 0 
                      ? "Select roles..." 
                      : newMemberForm.role.join(", ")
                    }
                  </span>
                  <ChevronDown className="h-4 w-4" />
                </label>
                <                ul tabIndex={0} className={`dropdown-content z-[1] menu p-2 shadow rounded-box w-full ${
                  isDarkMode ? "bg-brand-card-bg-glass border border-brand-border-logo" : "bg-base-100"
                }`}>
                  {["Dancer", "Teacher", "Director"].map((role) => (
                    <li key={role}>
                      <button
                        type="button"
                        onClick={() => toggleNewMemberRole(role)}
                        className={`flex items-center gap-2 ${
                          newMemberForm.role.includes(role) ? "active" : ""
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          newMemberForm.role.includes(role)
                            ? "border-primary bg-primary"
                            : isDarkMode ? "border-white/20" : "border-base-300"
                        }`}>
                          {newMemberForm.role.includes(role) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        {role}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* DOB */}
            <div className="form-control">
              <label className="label">
                <span className={`label-text ${isDarkMode ? "text-gray-300" : ""}`}>
                  Date of Birth {newMemberForm.role.includes("Dancer") && <span className="text-red-500">*</span>}
                </span>
              </label>
              <input
                type="date"
                value={newMemberForm.dateOfBirth}
                onChange={(e) => setNewMemberForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                className={`input input-bordered w-full ${
                  isDarkMode 
                                                  ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content" 
                              : ""
                }`}
                required={newMemberForm.role.includes("Dancer")}
                placeholder="mm-dd-yyyy"
              />
            </div>

            {/* Email */}
            <div className="form-control">
              <label className="label">
                <span className={`label-text ${isDarkMode ? "text-gray-300" : ""}`}>
                  Email (Optional)
                </span>
              </label>
              <input
                type="email"
                placeholder="Email address"
                value={newMemberForm.email}
                onChange={(e) => setNewMemberForm(prev => ({ ...prev, email: e.target.value }))}
                className={`input input-bordered w-full ${
                  isDarkMode 
                    ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content placeholder-gray-500" 
                    : ""
                }`}
              />
            </div>

            {/* Add Button */}
            <div className="form-control">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`btn btn-primary ${isSubmitting ? "loading" : ""}`}
              >
                {isSubmitting ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Main content area - flex-grow to push pagination down */}
      <div className="flex-grow flex flex-col">
        {/* Search input */}
        <div className="relative max-w-lg mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search
              className={`h-4 w-4 ${
                isDarkMode ? "text-gray-500" : "text-base-content/40"
              }`}
            />
          </div>
          <input
            type="text"
            placeholder="Search roster by name, role, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-lg ${
              isDarkMode
                ? "bg-brand-card-bg-glass border border-brand-border-logo text-brand-base-content placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-logo-purple focus:border-transparent transition-all duration-200"
                : "input input-bordered"
            }`}
          />
        </div>

        {/* The main roster table */}
        <div className="flex-grow min-h-0">
          <div
            className={`h-full overflow-x-auto rounded-lg border relative ${
              isDarkMode
                ? "bg-card-gradient backdrop-blur-sm border-brand-border-logo shadow-lg"
                : "bg-base-100 border-base-200"
            }`}
          >
        <table className="table w-full table-compact">
          <thead>
            <tr className={isDarkMode ? "border-brand-border-light" : ""}>
              {/* Number column */}
              <th className={isDarkMode ? "text-gray-400" : ""}>
                <div className="flex items-center justify-center">
                  #
                </div>
              </th>

              {/* Name column */}
              <th
                onClick={() => handleSort("name")}
                className={`cursor-pointer group ${
                  isDarkMode ? "text-gray-400" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  Member
                  <ArrowUpDown
                    className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "name" ? "opacity-100" : ""
                    }`}
                  />
                </div>
              </th>

              {/* Roles column */}
              <th
                onClick={() => handleSort("role")}
                className={`cursor-pointer group ${
                  isDarkMode ? "text-gray-400" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  Roles
                  <ArrowUpDown
                    className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "role" ? "opacity-100" : ""
                    }`}
                  />
                </div>
              </th>

              {/* Email column */}
              <th
                onClick={() => handleSort("email")}
                className={`cursor-pointer group ${
                  isDarkMode ? "text-gray-400" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  Email
                  <ArrowUpDown
                    className={`h-4 w-4 opacity-0 group-hover:opacity-50 ${
                      sortConfig.key === "email" ? "opacity-100" : ""
                    }`}
                  />
                </div>
              </th>

              {/* NEW: Routines column */}
              <th className={isDarkMode ? "text-gray-400" : ""}>
                Routines
              </th>

              {/* Actions column */}
              <th className={isDarkMode ? "text-gray-400" : ""}>
                <div className="flex items-center justify-between">
                  <span>Actions</span>
                  <div className="filter-container relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsFilterOpen(!isFilterOpen);
                      }}
                      className={`p-1.5 rounded-md transition-colors ${
                        isDarkMode 
                          ? "hover:bg-white/10" 
                          : "hover:bg-base-200"
                      } ${selectedRoles.length > 0 ? "text-blue-500" : ""}`}
                    >
                      <Filter className="h-4 w-4" />
                    </button>
                    {isFilterOpen && (
                      <div
                        className={`absolute right-0 top-full mt-2 w-48 rounded-lg shadow-lg z-10 ${
                          isDarkMode
                            ? "bg-brand-card-bg border border-brand-border-light"
                            : "bg-base-100 border border-base-200"
                        }`}
                      >
                        <div className="p-2 space-y-1">
                          {["Dancer", "Teacher", "Director"].map((role) => (
                            <button
                              key={role}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRole(role);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                                isDarkMode
                                  ? "hover:bg-white/5"
                                  : "hover:bg-base-200"
                              } ${
                                selectedRoles.includes(role) ? "text-blue-500" : ""
                              }`}
                            >
                              <div
                                className={`w-4 h-4 rounded border flex items-center justify-center ${
                                  selectedRoles.includes(role)
                                    ? "border-blue-500 bg-blue-500"
                                    : isDarkMode
                                    ? "border-white/20"
                                    : "border-base-300"
                                }`}
                              >
                                {selectedRoles.includes(role) && (
                                  <Check className="h-3 w-3 text-white" />
                                )}
                              </div>
                              {role}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            {currentPageItems.map((member, index) => {
              // Safety check to ensure we have valid member data
              if (!member || typeof member !== 'object') {
                console.warn('Invalid member data:', member);
                return null;
              }
              
              // 🚀 OPTIMIZED: Use pre-normalized roles, no more repeated normalization
              const roles = member.normalizedRoles || normalizeRoles(member.role);
              
              // Count how many routines this dancer is in:
              const dancerId = member.dancerId;
              const routineCount = countRoutines(dancerId);
              const isEditing = editingMemberId === dancerId;

              if (isEditing) {
                return (
                  <tr key={dancerId} className={`${isDarkMode ? "border-brand-border-light" : ""} bg-primary/5`}>
                    <td colSpan="6" className="p-4">
                      <form onSubmit={handleUpdateMember} className="space-y-4">
                        <div className="grid gap-3 items-end" style={{gridTemplateColumns: "auto 1fr 1fr 1fr 1fr 1fr 1fr auto"}}>
                          {/* Profile Photo Circle */}
                          <div className="form-control">
                            <label className="label">
                              <span className={`label-text text-xs ${isDarkMode ? "text-gray-300" : ""}`}>
                                Photo
                              </span>
                            </label>
                            <div className="flex">
                              {editMemberForm.profilePicture ? (
                                <div className="relative">
                                  <img
                                    src={editMemberForm.profilePicture}
                                    alt="Profile"
                                    className="w-10 h-10 rounded-full object-cover border-2 border-primary"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setEditMemberForm(prev => ({ ...prev, profilePicture: "" }))}
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-error text-white rounded-full flex items-center justify-center text-xs hover:bg-error/80 transition-colors"
                                    title="Remove photo"
                                  >
                                    <X className="w-2 h-2" />
                                  </button>
                                </div>
                              ) : (
                                <div className={`w-10 h-10 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-primary transition-colors ${
                                  isDarkMode 
                                    ? "border-gray-600 bg-[#1a1f2d] hover:border-primary" 
                                    : "border-gray-300 bg-base-100 hover:border-primary"
                                }`}
                                onClick={() => document.getElementById('edit-photo-input').click()}
                                title="Add photo">
                                  <Plus className={`w-4 h-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
                                </div>
                              )}
                              {/* Hidden file input for edit form */}
                              <input
                                id="edit-photo-input"
                                type="file"
                                accept="image/*"
                                onChange={handleEditPhotoUpload}
                                className="hidden"
                              />
                            </div>
                          </div>

                          {/* First Name */}
                          <div className="form-control">
                            <label className="label">
                              <span className={`label-text text-xs ${isDarkMode ? "text-gray-300" : ""}`}>
                                First Name
                              </span>
                            </label>
                            <input
                              type="text"
                              value={editMemberForm.firstName}
                              onChange={(e) => setEditMemberForm(prev => ({ ...prev, firstName: e.target.value }))}
                              className={`input input-sm input-bordered w-full ${
                                isDarkMode 
                                                                ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content" 
                              : ""
                              }`}
                              required
                            />
                          </div>

                          {/* Last Name */}
                          <div className="form-control">
                            <label className="label">
                              <span className={`label-text text-xs ${isDarkMode ? "text-gray-300" : ""}`}>
                                Last Name
                              </span>
                            </label>
                            <input
                              type="text"
                              value={editMemberForm.lastName}
                              onChange={(e) => setEditMemberForm(prev => ({ ...prev, lastName: e.target.value }))}
                              className={`input input-sm input-bordered w-full ${
                                isDarkMode 
                                                                ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content" 
                              : ""
                              }`}
                              required
                            />
                          </div>

                          {/* Gender */}
                          <div className="form-control">
                            <label className="label">
                              <span className={`label-text text-xs ${isDarkMode ? "text-gray-300" : ""}`}>
                                Gender
                              </span>
                            </label>
                            <select
                              value={editMemberForm.gender || ""}
                              onChange={(e) => setEditMemberForm(prev => ({ ...prev, gender: e.target.value }))}
                              className={`select select-sm select-bordered w-full ${
                                isDarkMode 
                                                                ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content" 
                              : ""
                              }`}
                            >
                              <option value="">Gender</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                              <option value="Non-binary">Non-binary</option>
                              <option value="Prefer not to say">Prefer not to say</option>
                            </select>
                          </div>

                          {/* Roles */}
                          <div className="form-control">
                            <label className="label">
                              <span className={`label-text text-xs ${isDarkMode ? "text-gray-300" : ""}`}>
                                Roles
                              </span>
                            </label>
                            <div className="dropdown dropdown-bottom">
                              <label tabIndex={0} className={`btn btn-sm btn-outline w-full justify-between ${
                                isDarkMode ? "border-white/10 hover:bg-white/5" : ""
                              }`}>
                                <span className="truncate text-xs">
                                  {(!editMemberForm.role || editMemberForm.role.length === 0)
                                    ? "Select roles..." 
                                    : (Array.isArray(editMemberForm.role) ? editMemberForm.role : normalizeRoles(editMemberForm.role)).join(", ")
                                  }
                                </span>
                                <ChevronDown className="h-3 w-3" />
                              </label>
                              <                ul tabIndex={0} className={`dropdown-content z-[1] menu p-2 shadow rounded-box w-full ${
                  isDarkMode ? "bg-brand-card-bg-glass border border-brand-border-logo" : "bg-base-100"
                }`}>
                                {["Dancer", "Teacher", "Director"].map((role) => (
                                  <li key={role}>
                                    <button
                                      type="button"
                                      onClick={() => toggleEditMemberRole(role)}
                                      className={`flex items-center gap-2 text-xs ${
                                        editMemberForm.role?.includes(role) ? "active" : ""
                                      }`}
                                    >
                                      <div className={`w-3 h-3 rounded border flex items-center justify-center ${
                                        editMemberForm.role?.includes(role)
                                          ? "border-primary bg-primary"
                                          : isDarkMode ? "border-white/20" : "border-base-300"
                                      }`}>
                                        {editMemberForm.role?.includes(role) && (
                                          <Check className="h-2 w-2 text-white" />
                                        )}
                                      </div>
                                      {role}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {/* DOB */}
                          <div className="form-control">
                            <label className="label">
                              <span className={`label-text text-xs ${isDarkMode ? "text-gray-300" : ""}`}>
                                Date of Birth
                              </span>
                            </label>
                            <input
                              type="date"
                              value={editMemberForm.dateOfBirth || ""}
                              onChange={(e) => setEditMemberForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                              className={`input input-sm input-bordered w-full ${
                                isDarkMode 
                                                                ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content" 
                              : ""
                              }`}
                            />
                          </div>

                          {/* Actions */}
                          <div className="form-control">
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`btn btn-sm btn-primary ${isSubmitting ? "loading" : ""}`}
                              >
                                {isSubmitting ? "Saving..." : "Save"}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="btn btn-sm btn-ghost"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Email row */}
                        <div className="grid gap-3" style={{gridTemplateColumns: "auto 1fr"}}>
                          <div></div> {/* Empty space for photo column */}
                          <div className="form-control">
                            <label className="label">
                              <span className={`label-text text-xs ${isDarkMode ? "text-gray-300" : ""}`}>
                                Email (Optional)
                              </span>
                            </label>
                            <input
                              type="email"
                              value={editMemberForm.email || ""}
                              onChange={(e) => setEditMemberForm(prev => ({ ...prev, email: e.target.value }))}
                              className={`input input-sm input-bordered w-full ${
                                isDarkMode 
                                                                ? "bg-brand-card-bg-glass border-brand-border-logo text-brand-base-content" 
                              : ""
                              }`}
                            />
                          </div>
                        </div>
                      </form>
                    </td>
                  </tr>
                );
              }

              return (
                <tr 
                  key={dancerId} 
                  className={`
                    group transition-colors
                    ${isDarkMode 
                      ? "hover:bg-white/5 border-b border-white/10" 
                      : "hover:bg-gray-50 border-b border-gray-200"}
                    ${index !== currentPageItems.length - 1 
                      ? isDarkMode 
                        ? "border-b border-white/10" 
                        : "border-b border-gray-200"
                      : ""}
                  `}
                >
                  {/* Number */}
                  <td className={`text-center font-mono text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    {startIndex + index + 1}
                  </td>

                  {/* Name + avatar */}
                  <td
                    className={`flex items-center gap-3 ${
                      isDarkMode ? "text-gray-400" : ""
                    }`}
                  >
                    {member.profilePicture ? (
                      <img
                        src={member.profilePicture}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-base-200 flex items-center justify-center">
                        {member.firstName?.[0]}
                        {member.lastName?.[0]}
                      </div>
                    )}
                    <div>
                      <div className="font-medium">
                        {(member.firstName || '')} {(member.lastName || '')}
                      </div>
                      {roles.includes("Dancer") && member.dateOfBirth && (
                        <div className="text-sm opacity-70">
                          Age: {calculateAge(member.dateOfBirth)}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Roles */}
                  <td className={isDarkMode ? "text-gray-400" : ""}>
                    <div className="flex flex-wrap gap-1">
                      {roles.map((role, index) => (
                        <span
                          key={`${role}-${index}`}
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                            role
                          )}`}
                        >
                          {typeof role === 'object' ? JSON.stringify(role) : String(role)}
                        </span>
                      ))}
                    </div>
                  </td>

                  {/* Email */}
                  <td className={isDarkMode ? "text-gray-400" : ""}>
                    {member.email || ''}
                  </td>

                  {/* 🚀 ENHANCED: Underlined clickable Routines count with hover preview */}
                  <td className={`relative ${isDarkMode ? "text-gray-400" : ""}`}>
                    {routineCount > 0 ? (
                      <div className="relative">
                        <button
                          onClick={() => handleRoutineCountClick(member)}
                          onMouseEnter={() => handleRoutineHover(member)}
                          onMouseLeave={handleRoutineHoverLeave}
                          className={`underline decoration-1 underline-offset-2 transition-colors hover:decoration-2 ${
                            isDarkMode 
                              ? "text-blue-400 hover:text-blue-300 decoration-blue-400 hover:decoration-blue-300" 
                              : "text-blue-600 hover:text-blue-700 decoration-blue-600 hover:decoration-blue-700"
                          }`}
                          title={`View ${routineCount} routine${routineCount !== 1 ? 's' : ''}`}
                        >
                          {routineCount}
                        </button>
                        
                        {/* 🎭 Hover Preview Tooltip - Desktop Only */}
                        {supportsHover && hoveredMember?.dancerId === member.dancerId && (
                          <div 
                            className={`absolute z-50 top-full left-1/2 transform -translate-x-1/2 mt-2 w-72 rounded-lg shadow-lg border glass-card ${
                              isDarkMode
                                ? "border-brand-border-electric text-brand-base-content"
                                : "bg-white border-gray-200 text-gray-900"
                            }`}
                            onMouseEnter={handleTooltipHover}
                            onMouseLeave={handleTooltipLeave}
                          >
                            <div className="p-3">
                              <div className={`text-xs font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                                {member.firstName}'s Routines:
                              </div>
                              
                              {/* Scrollable routine list */}
                              <div 
                                className={`max-h-32 overflow-y-auto space-y-1 ${
                                  getDancerRoutines(member.dancerId).length > 4 ? 'pr-2' : ''
                                } scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent`}
                                style={{ scrollbarWidth: 'thin' }}
                              >
                                {getDancerRoutines(member.dancerId).map((routine, idx) => (
                                  <div key={idx} className={`text-sm flex items-start gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-700"}`}>
                                    <span className="text-xs mt-1">•</span>
                                    <span className="flex-1">{routine.title || 'Untitled Routine'}</span>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Combined scroll hint and click instruction */}
                              <div className={`text-xs pt-2 mt-2 border-t ${isDarkMode ? "border-white/10 text-gray-500" : "border-gray-200 text-gray-500"} text-center`}>
                                {getDancerRoutines(member.dancerId).length > 4 
                                  ? `↕ Scroll to see all ${getDancerRoutines(member.dancerId).length} • Click number for details`
                                  : 'Click number for full details'
                                }
                              </div>
                            </div>
                            
                            {/* Triangle pointer */}
                            <div 
                              className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent ${
                                isDarkMode ? "border-b-[#1a1f2d]" : "border-b-white"
                              }`}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        0
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className={isDarkMode ? "text-gray-400" : ""}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditClick(member)}
                        className={`btn btn-ghost btn-sm ${
                          isDarkMode ? "text-gray-400" : ""
                        }`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMember(member);
                          setIsDeleteModalOpen(true);
                        }}
                        className={`btn btn-ghost btn-sm text-error hover:bg-error/10 ${
                          isDarkMode ? "text-gray-400" : ""
                        }`}
                        title="Delete member"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
          </div>
        </div>
      </div>

      {/* Pagination Controls - Sticky at bottom */}
      {totalPages > 1 && (
        <div className={`flex-shrink-0 flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 p-3 rounded-lg ${
          isDarkMode 
            ? "bg-brand-card-bg/80 backdrop-blur-sm border border-brand-border-light" 
            : "bg-base-100 border border-base-200"
        }`}>
          {/* Items per page selector */}
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              Show:
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className={`select select-sm select-bordered ${
                isDarkMode 
                  ? "bg-[#1a1f2d] border-white/10 text-white" 
                  : ""
              }`}
            >
              <option value={4}>4</option>
              <option value={8}>8</option>
              <option value={12}>12</option>
              <option value={16}>16</option>
              <option value={20}>20</option>
            </select>
            <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              per page
            </span>
          </div>

          {/* Page info */}
          <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} members
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`btn btn-sm btn-ghost ${
                currentPage === 1 ? "btn-disabled" : ""
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`btn btn-sm ${
                      currentPage === pageNumber
                        ? "btn-primary"
                        : `btn-ghost ${isDarkMode ? "hover:bg-white/10" : ""}`
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`btn btn-sm btn-ghost ${
                currentPage === totalPages ? "btn-disabled" : ""
              }`}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* 🗑️ Delete Confirmation Modal with Routine Impact */}
      <FormModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-warning" />
            <span>{selectedMember ? `Delete ${selectedMember.firstName} ${selectedMember.lastName}?` : "Delete Dancer?"}</span>
          </div>
        }
        className="max-w-4xl w-full"
      >
        <div className="space-y-4">
          {selectedMember && (() => {
            const routineInfo = getMemberRoutinesForDeletion(selectedMember);
            
            return (
              <div>
                <div className="mb-4">
                  {/* Prominent red notice */}
                  <p className={`text-sm font-medium mb-3 ${isDarkMode ? "text-red-400" : "text-red-600"}`}>
                    Note: Removing a dancer does not remove them from any existing registrations
                  </p>
                    
                  {routineInfo.hasRoutines ? (
                    <div className="space-y-2">
                      <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                        Deleting this dancer will also <strong>remove them from {routineInfo.totalRoutines} routine{routineInfo.totalRoutines !== 1 ? 's' : ''}</strong> in your account:
                      </p>
                      
                      {/* Routine List - 4 Column Grid */}
                      <div className={`p-4 rounded-lg ${
                        isDarkMode ? "bg-[#1a1f2d]/50" : "bg-gray-50"
                      }`}>
                        <div className="grid grid-cols-4 gap-x-4 gap-y-2">
                          {routineInfo.memberRoutines.map((routine, idx) => (
                            <div key={idx} className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                              • {routine.title || 'Untitled Routine'}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                      This dancer is not currently in any routines.
                    </p>
                  )}
                </div>

                {/* Confirmation */}
                <div className="flex justify-between gap-3 pt-2">
                  <button
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleDeleteMember} 
                    className="btn btn-error"
                  >
                    {routineInfo.hasRoutines 
                      ? `Delete & Remove from ${routineInfo.totalRoutines} Routine${routineInfo.totalRoutines !== 1 ? 's' : ''}`
                      : 'Delete Dancer'
                    }
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </FormModal>

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        onImport={handleBulkImport}
        isSubmitting={isSubmitting}
        existingRoster={roster}
        importProgress={importProgress}
      />

      {/* 🚀 NEW: Routine Details Modal */}
      <FormModal
        isOpen={isRoutineModalOpen}
        onClose={() => setIsRoutineModalOpen(false)}
        title={`Routines for ${selectedMember?.firstName} ${selectedMember?.lastName}`}
      >
        <div className="space-y-4">
          {selectedMemberRoutines.length === 0 ? (
            <div className="text-center py-8">
              <Users className={`mx-auto h-12 w-12 mb-4 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
              <p className={`text-lg font-medium ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                No routines found
              </p>
              <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                This member is not assigned to any routines yet.
              </p>
            </div>
          ) : (
            <>
              <div className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-4`}>
                {selectedMember?.firstName} is performing in {selectedMemberRoutines.length} routine{selectedMemberRoutines.length !== 1 ? 's' : ''}:
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {selectedMemberRoutines.map((routine, index) => (
                  <div
                    key={routine.routineId || index}
                    className={`p-4 rounded-lg border transition-colors ${
                      isDarkMode
                        ? "bg-[#1a1f2d]/50 border-white/10 hover:bg-[#1a1f2d]/70"
                        : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className={`font-semibold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                          {routine.title || 'Untitled Routine'}
                        </h4>
                        
                        {routine.description && (
                          <p className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                            {routine.description}
                          </p>
                        )}
                        
                        <div className="mt-3 space-y-2">
                          {/* Music Information */}
                          <div className={`flex items-center gap-2 text-xs`}>
                            <Music className="h-3 w-3" />
                            {routine.songFileName ? (
                              <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                                Music: {routine.songFileName}
                              </span>
                            ) : (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                isDarkMode
                                  ? "bg-red-500/10 text-red-400"
                                  : "bg-red-100 text-red-700"
                              }`}>
                                No music uploaded
                              </span>
                            )}
                          </div>
                          
                          {/* Dancer Count */}
                          <div className={`flex items-center gap-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                            <Users className="h-3 w-3" />
                            <span>{normalizeDancerList(routine.dancers).length} dancer{normalizeDancerList(routine.dancers).length !== 1 ? 's' : ''} total</span>
                          </div>
                          
                          {/* Creation Date */}
                          {routine.createdAt && (
                            <div className={`flex items-center gap-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                              <Calendar className="h-3 w-3" />
                              <span>Created: {new Date(routine.createdAt).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setIsRoutineModalOpen(false)}
              className="btn btn-primary"
            >
              Close
            </button>
          </div>
        </div>
      </FormModal>

      {/* Error Alert */}
      {error && (
        <div
          className={`alert alert-error mt-4 ${
            isDarkMode
              ? "bg-red-500/10 border border-red-500/20 text-red-400"
              : ""
          }`}
        >
          <AlertCircle
            className={`h-5 w-5 ${isDarkMode ? "text-red-400" : ""}`}
          />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className={`btn btn-ghost btn-sm ${
              isDarkMode ? "text-red-400" : ""
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default StudioPage;
